import { getUniqueNameWithCopyCounter } from '@bpinternal/shared'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { FileType, ScopedClient } from '../types'

const MAX_FILES = 8

type FileItem = {
  fileId?: string
  name: string
  type: FileType
  url?: string
  instance: File
  status: 'loading' | 'error' | 'uploaded'
}

type ComposerFileStore = {
  files: FileItem[]
  setFiles: (files: FileItem[]) => void
  upsertFile: (file: FileItem) => void
  uploadFile: (file: File, uploadFile: ScopedClient['uploadFile']) => void
  deleteFile: (filename: string) => void
}

const createComposerFileStore = (name: string) =>
  create<ComposerFileStore>()(
    persist(
      (set, get) => ({
        files: [],
        setFiles: (files) => set({ files }),
        upsertFile: (incomingFile) =>
          set(({ files }) => {
            const fileExists = files.some((f) => f.name === incomingFile.name)

            const filesArr = fileExists
              ? files.map((file) => (file.name === incomingFile.name ? { ...file, ...incomingFile } : file))
              : [...files, { ...incomingFile }]

            return { files: filesArr }
          }),
        uploadFile: async (file, uploadFile) => {
          if (get().files.length === MAX_FILES) return

          const lastDotIndex = file.name.lastIndexOf('.')
          const hasExtension = lastDotIndex > 0 && lastDotIndex < file.name.length - 1

          const nameWithoutExt = hasExtension ? file.name.substring(0, lastDotIndex) : file.name
          const extension = hasExtension ? file.name.substring(lastDotIndex) : ''

          const uniqueNameWithoutExt = getUniqueNameWithCopyCounter(nameWithoutExt, get().files, (f) => f.name)

          const fullFileName = uniqueNameWithoutExt + extension

          const fileWithName = new File([file], fullFileName, { type: file.type })
          get().upsertFile({
            name: uniqueNameWithoutExt,
            type: 'file',
            instance: fileWithName,
            status: 'loading',
          })
          try {
            const { fileUrl: url, type, fileId } = await uploadFile(fileWithName)

            get().upsertFile({
              fileId: fileId,
              name: uniqueNameWithoutExt,
              type: type,
              url: url,
              instance: fileWithName,
              status: 'uploaded',
            })
          } catch (_) {
            get().upsertFile({
              name: uniqueNameWithoutExt,
              type: 'file',
              instance: fileWithName,
              status: 'error',
            })
          }
        },
        deleteFile: (filename) =>
          set(({ files }) => {
            return { files: files.filter((file) => file.name != filename) }
          }),
      }),
      {
        name: name,
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )

type BoundComposerFileStore = ReturnType<typeof createComposerFileStore>

const composerFileStoreInstances: { [key: string]: BoundComposerFileStore } = {}

export const getComposerFileStore = (conversationId: string, storageKey?: string): BoundComposerFileStore => {
  const baseKey = storageKey || 'bp-webchat'
  const storeKey = `${baseKey}-composer-files-${conversationId}`

  if (!composerFileStoreInstances[storeKey]) {
    composerFileStoreInstances[storeKey] = createComposerFileStore(storeKey)
  }
  return composerFileStoreInstances[storeKey]
}

/**
 * Clears all cached composer file store instances.
 * Useful for testing to ensure clean state between test runs.
 */
export const clearComposerFileStoreCache = () => {
  Object.keys(composerFileStoreInstances).forEach((key) => {
    delete composerFileStoreInstances[key]
  })
}
